import React from 'react';

import BooleanSearchWidgets from 'components/pure/search/BooleanSearchWidgets';
import ComboboxSearchWidgets from 'components/pure/search/ComboboxSearchWidgets';
import DateSearchWidgets from 'components/pure/search/DateSearchWidgets';
import EnumSearchWidgets from 'components/pure/search/EnumSearchWidgets';
import NumericSearchWidgets from 'components/pure/search/NumericSearchWidgets';
import StringSearchWidgets from 'components/pure/search/StringSearchWidgets';
import RadioButtonsSearchWidgets from 'components/pure/search/RadioButtonsSearchWidgets';
import PhoneNumberSidebarSearchWidgets from 'components/pure/search/phone/PhoneNumberSidebarSearchWidgets';
import KeywordTopicSearchWidgets from './keyword-topic/KeywordTopicSearchWidgets';
import {SEARCH_FIELD_WIDGET_TYPES} from 'utils/const';
import immutable, {List} from 'immutable';

require('styles/SearchWidgets.styl');

/**
 * `SearchWidgets` is the parent container component for all the `SearchWidget`
 * components. It determines the quantity and number of `SearchWidget` being
 * displayed, especially for custom `SearchWidget` components that are
 * dynamically composed from custom `SearchField` obtained from the database
 */
export default class SearchWidgets extends React.Component {
  constructor() {
    super();
    this.state = {
      lastToggledOnSearchWidget: null,
    };
    this.updateLastToggledOnSearchWidget = this.updateLastToggledOnSearchWidget.bind(this);
  }

  /**
   * Updates lastToggledOnSearchWidget to the most recently toggled on search field name.
   * @param {string} lastToggledOnSearchWidgetName of the search field that was toggled on.
   */
  updateLastToggledOnSearchWidget(lastToggledOnSearchWidgetName) {
    this.setState({lastToggledOnSearchWidget: lastToggledOnSearchWidgetName});
  }

  /**
   * Returns a boolean that indicates whether search widgets should be sorted by their toggled state.
   * @return {boolean} True if search widgets should sort by toggled state.
   */
  shouldSortByToggled() {
    return this.props.showTrendChart;
  }

  /**
   * Returns a boolean that indicates whether the group should be rendered on this search field.
   * Only the search field with the lowest item in the group of search fields should trigger a render.
   * This is to be able to walk the list of all search fields and only render on the first item from a group.
   * @return {boolean} True if the search field group should be rendered.
   */
  shouldRenderSearchFieldGroup(searchField, searchFieldList) {
    // if the group id is null then it should render because it's not in a group to have been rendered by another sf
    if (searchField.group_id === null) {
      return true;
    }
    // first get just the ones with the same group id, then order so that the first item has the lowest order value
    const searchFieldWithLowestOrder = this.getSearchFieldsWithGroupId(searchField, searchFieldList)
      .sort((a, b) => (a.order > b.order ? 1 : -1))
      .first();
    return searchField.order <= searchFieldWithLowestOrder.order; // no two sf should have the same order
  }

  /**
   * Get all items in a search field group for a given search field type.
   * @param  {immutable.List}  `Search.Field` the search field whose group / type you are looking for.
   * @return {immutable.List}  A list of `Search.Field` objects whose type is `type`
   */
  getSearchFieldsWithGroupId(searchField, searchFieldList) {
    if (searchField.group_id === null) {
      return immutable.List([searchField]);
    }
    return searchFieldList.filter(sf => {
      return sf.group_id === searchField.group_id && sf.type === searchField.type;
    });
  }

  /**
   * Get key for group search fields.
   * group id when it exists and name when group id is null
   */
  getKeyForGroupSearchFields(searchField) {
    if (searchField.group_id === null) {
      return searchField.name;
    } else {
      return searchField.group_id;
    }
  }

  /**
   * Return the rendered search widgets with or without toggled widgets at the top based on shouldSortByToggled.
   * Making two calls to render the search fields set allows for rendering toggled options first
   * To avoid duplicates, the second pass of renderSearchWidgets needs to be opposite params of the first pass.
   *
   * @return parent container and contents for search widgets.
   */
  renderOrderedSearchWidgets() {
    // When shouldRenderToggledFirstPass is true all toggled on
    // widgets are sorted to the top. When it is false rendered at
    // bottom/default order
    const shouldRenderToggledFirstPass = true;

    let shouldRenderNotToggledFirstPass;
    if (this.shouldSortByToggled()) {
      shouldRenderNotToggledFirstPass = !shouldRenderToggledFirstPass;
    } else {
      shouldRenderNotToggledFirstPass = shouldRenderToggledFirstPass;
    }

    return (
      <div className="search-widgets-container">
        {this.renderSearchWidgets(shouldRenderToggledFirstPass, shouldRenderNotToggledFirstPass)}
        {this.renderSearchWidgets(!shouldRenderToggledFirstPass, !shouldRenderNotToggledFirstPass)}
      </div>
    );
  }

  /**
   * Note that each item needs a key to avoid a console warning because this is a list of react objects.
   * Keys for booleans should be the group id, all other keys are the search field name.
   * If any search fields other than boolean implement grouping in the future, the key will need to be changed to
   * group id.
   */
  renderWidgetByType(sf, commonParameters) {
    const sfAsList = List([sf]);
    switch (sf.widgetType) {
      case SEARCH_FIELD_WIDGET_TYPES.KEYWORD_TOPIC:
        return (
          <KeywordTopicSearchWidgets
            key={sf.name}
            searchFields={sfAsList}
            searchFieldsState={this.props.keywordTopicSearchFieldsState}
            searchFieldsToggled={this.props.keywordTopicSearchFieldsToggled}
            flags={this.props.user.flags}
            {...commonParameters}
          />
        );
      case SEARCH_FIELD_WIDGET_TYPES.PHONE:
        return (
          <PhoneNumberSidebarSearchWidgets
            key={sf.name}
            searchFields={sfAsList}
            searchFieldsState={this.props.phoneSearchFieldsState}
            searchFieldsToggled={this.props.phoneSearchFieldsToggled}
            {...commonParameters}
          />
        );
      case SEARCH_FIELD_WIDGET_TYPES.BOOLEAN:
        return this.shouldRenderSearchFieldGroup(sf, this.props.booleanSearchFields) ? (
          <BooleanSearchWidgets
            key={this.getKeyForGroupSearchFields(sf)}
            searchFields={this.getSearchFieldsWithGroupId(sf, this.props.booleanSearchFields)}
            searchFieldsState={this.props.booleanSearchFieldsState}
            toggled={this.props.booleanSearchFieldsToggled}
            {...commonParameters}
          />
        ) : null;
      case SEARCH_FIELD_WIDGET_TYPES.NUMERIC:
        return (
          <NumericSearchWidgets
            key={sf.name}
            searchFields={sfAsList}
            searchFieldsState={this.props.numericSearchFieldsState}
            searchFieldsToggled={this.props.numericSearchFieldsToggled}
            {...commonParameters}
          />
        );
      case SEARCH_FIELD_WIDGET_TYPES.DATETIME:
        return (
          <DateSearchWidgets
            key={sf.name}
            searchFields={sfAsList}
            searchFieldsState={this.props.datetimeSearchFieldsState}
            searchFieldsToggled={this.props.datetimeSearchFieldsToggled}
            {...commonParameters}
          />
        );
      case SEARCH_FIELD_WIDGET_TYPES.ENUM:
        return (
          <EnumSearchWidgets
            key={sf.name}
            searchFields={sfAsList}
            searchFieldsState={this.props.enumSearchFieldsState}
            searchFieldsToggled={this.props.enumSearchFieldsToggled}
            {...commonParameters}
          />
        );
      case SEARCH_FIELD_WIDGET_TYPES.FINITE_STRING:
        return (
          <StringSearchWidgets
            key={sf.name}
            searchFields={sfAsList}
            searchFieldsState={this.props.stringSearchFieldsState}
            searchFieldsToggled={this.props.stringSearchFieldsToggled}
            isSearchingLibraryTags={this.props.isSearchingLibraryTags}
            isSearchingSelf={this.props.isSearchingSelf}
            {...commonParameters}
          />
        );
      case SEARCH_FIELD_WIDGET_TYPES.INFINITE_STRING:
        return (
          <StringSearchWidgets
            key={sf.name}
            searchFields={sfAsList}
            searchFieldsState={this.props.stringSearchFieldsState}
            searchFieldsToggled={this.props.stringSearchFieldsToggled}
            isSearchingLibraryTags={this.props.isSearchingLibraryTags}
            isSearchingSelf={this.props.isSearchingSelf}
            {...commonParameters}
          />
        );
      case SEARCH_FIELD_WIDGET_TYPES.COMBO_BOX:
        return this.props.isSearchingSelf ||
          userHasViewSelfPermissionOnly(this.props.user) ? null : (
          <ComboboxSearchWidgets
            key={sf.name}
            searchFields={sfAsList}
            searchFieldsState={this.props.comboboxSearchFieldsState}
            searchFieldsToggled={this.props.comboboxSearchFieldsToggled}
            {...commonParameters}
          />
        );
      case SEARCH_FIELD_WIDGET_TYPES.RADIO_BUTTON:
        return this.props.isSearchingSelf ||
          userHasViewSelfPermissionOnly(this.props.user) ? null : (
          <RadioButtonsSearchWidgets
            key={sf.name}
            searchFields={sfAsList}
            searchFieldsState={this.props.radioButtonsSearchFieldsState}
            searchFieldsToggled={this.props.radioButtonsSearchFieldsToggled}
            flags={this.props.user.flags}
            {...commonParameters}
          />
        );
      default:
        return null;
    }
  }

  /**
   * Return a single set of search widgets filtered by the should render toggled or not criterion.
   *
   * @return span containing the different types of search widgets.
   */
  renderSearchWidgets(shouldRenderToggled, shouldRenderNotToggled) {
    const commonParameters = {
      shouldSortByToggled: this.shouldSortByToggled(),
      lastToggledOnSearchWidget: this.state.lastToggledOnSearchWidget,
      updateLastToggledOnSearchWidget: this.updateLastToggledOnSearchWidget,
      shouldRenderToggled: shouldRenderToggled,
      shouldRenderNotToggled: shouldRenderNotToggled,
    };

    // recombine all of the types of search fields
    // TODO could refactor SearchWidgetsContainer so that it doesn't pass search fields separated by type.
    const allSearchFieldsOrdered = this.props.phoneSearchFields
      .concat(
        this.props.keywordTopicSearchFields,
        this.props.booleanSearchFields,
        this.props.numericSearchFields,
        this.props.datetimeSearchFields,
        this.props.enumSearchFields,
        this.props.stringSearchFields,
        this.props.comboboxSearchFields,
        this.props.radioButtonsSearchFields
      )
      .sort((a, b) => (a.order > b.order ? 1 : -1));

    let searchFieldWidgetList = [];
    allSearchFieldsOrdered.forEach(searchField => {
      searchFieldWidgetList.push(this.renderWidgetByType(searchField, commonParameters));
    });

    return <span>{searchFieldWidgetList}</span>;
  }

  render() {
    if (!(this.props.isTermBasedSearch && this.props.notEnoughSearchResults)) {
      return this.renderOrderedSearchWidgets();
    }
    return null;
  }
}

function userHasViewSelfPermissionOnly(user) {
  const viewPerms = user.permissions.filter(perm => perm.startsWith('view_'));
  return viewPerms.includes('view_self') && viewPerms.length === 1;
}
